<template>
  <div v-if="disabled">
    {{ plateNo }}
  </div>
  <QS-select
    v-else
    v-model="selectedCarId"
    :placeholder="placeholder"
    filterable
    remote
    :clearable="clearable"
    :multiple="multiple"
    :remote-method="getPlateNoList"
    :option-data="mergedOptions"
    :option-config="optionConfig"
    :loading="loading"
    @change="getCarInfo"
    popper-class="car-qs-select-list"
  >
  </QS-select>
</template>
<script setup>
import { ref, watch, onBeforeUnmount, onMounted, computed } from 'vue'
import { getSimpleCarList, getCarDetailById } from '@/api/carManage/index.js'
import { debounce } from 'lodash'

const props = defineProps({
  placeholder: {
    type: String,
    default: '请输入',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  clearable: {
    type: Boolean,
    default: true,
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  // 请求车辆列表的额外参数，组件内请求接口使用的参数为plateNo（车牌号）state (1-全部车辆)
  getListParams: {
    type: Object,
    default: () => ({}),
  },
  // 是否要获取车辆详情信息，比如车型、车系等
  getCarDetail: {
    type: Boolean,
    default: true,
  },
  modelValue: {
    type: [String, Number],
    default: '',
  },
  plateNo: {
    type: String,
    default: '',
  },
  optionConfig: {
    type: Object,
    default: () => ({
      labelKey: 'plateNo',
      valueKey: 'id',
    }),
  },
  // 额外选项，用于回显时确保选中项在下拉列表中
  extraOptions: {
    type: Array,
    default: () => [],
  },
})
const plateNoList = ref([])
const loading = ref(false)
// 车辆详情信息
const carInfo = ref({})
const selectedCarId = ref('')
const selectedPlateNo = ref('')
// 初次请求获取的车辆列表
const initCarList = ref([])

// 合并后的选项列表，确保 extraOptions 中的选项始终存在
const mergedOptions = computed(() => {
  if (!props.extraOptions || props.extraOptions.length === 0) {
    return plateNoList.value
  }
  const valueKey = props.optionConfig.valueKey || 'id'
  const existingIds = new Set(plateNoList.value.map((item) => String(item[valueKey])))
  const extras = props.extraOptions.filter((item) => !existingIds.has(String(item[valueKey])))
  return [...extras, ...plateNoList.value]
})

const emit = defineEmits(['change', 'update:modelValue'])
// 车牌号远程搜索
const getPlateNoList = (query) => {
  if (!query || query.trim().length < 1) {
    return
  }
  getCarList(query.trim())
}

const getCarList = async (query) => {
  loading.value = true
  try {
    const { code, data } = await getSimpleCarList({
      plateNo: query,
      state: 2, // 默认是运营中车辆
      ...props.getListParams,
    })
    if (code === 1) {
      plateNoList.value = data || []
    }
  } finally {
    loading.value = false
  }
}

const getCarInfo = async (carId) => {
  emit('update:modelValue', carId)
  if (!carId) {
    plateNoList.value = initCarList.value
    carInfo.value = {}
    emit('change', carId, carInfo.value)
    return
  }
  let data = {}
  if (props.getCarDetail && carId) {
    const res = await getCarDetailById({ carId })
    data = res.data || {}
  }
  carInfo.value = data || {}
  selectedPlateNo.value = data?.plateNo || ''
  emit('change', carId, carInfo.value)
}

const handlePlateNo = debounce(async (val) => {
  await getCarList(val)
  let filterItem = null
  if (plateNoList.value.length > 0) {
    filterItem = plateNoList.value.find((item) => item.plateNo === val)
    selectedCarId.value = filterItem ? filterItem.id : ''
    // 确保在通过plateNo设置值时也触发更新事件
    emit('update:modelValue', selectedCarId.value)
  }
  if (props.getCarDetail && filterItem && filterItem.id) {
    getCarInfo(filterItem.id)
  }
}, 500)

watch(() => props.modelValue, (val) => {
  selectedCarId.value = val
  if (!val) {
    plateNoList.value = initCarList.value
    carInfo.value = {}
  }
}, { immediate: true })

watch(() => props.plateNo, async (val) => {
  if (!val) {
    return
  }
  if (selectedPlateNo.value !== val) {
    handlePlateNo(val)
  }
}, { immediate: true })

onBeforeUnmount(() => {
  plateNoList.value = initCarList.value
  carInfo.value = {}
})

onMounted(async () => {
  await getCarList()
  initCarList.value = plateNoList.value
})
</script>
<script>
export default {
  name: 'CarSelect',
}
</script>
<style lang="scss">
.car-qs-select-list {
  .el-select-dropdown__wrap {
    width: 100% !important;
    .qs-select-dropdown__item {
      max-width: 100% !important;
    }
  }
}
</style>
